import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, Download, Upload, Shield, Package, Clock, Briefcase,
  FileSpreadsheet, QrCode
} from "lucide-react";

// Tab-komponenter
import FilerTab from "./stamdata/FilerTab";
import VagterTab from "./stamdata/VagterTab";
import FunktionerTab from "./stamdata/FunktionerTab";
import FrivilligeTab from "./stamdata/FrivilligeTab";
import TeamsTab from "./stamdata/TeamsTab";
import AdgangTab from "./stamdata/AdgangTab";
import RessourcerTab from "./stamdata/RessourcerTab";
import HurtigIndtjekningTab from "./stamdata/HurtigIndtjekningTab";

export default function Stamdata({ user, onLogout, selectedEvent }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("filer");
  const fileInputRef = useRef(null);
  
  const token = localStorage.getItem("token");
  const eventId = selectedEvent?.id || localStorage.getItem("selectedEventId");
  const eventName = selectedEvent?.name || localStorage.getItem("selectedEventName") || "Event";
  
  const isAdmin = user?.role === 'admin';
  const isArrangør = user?.role === 'arrangør';

  // Tab-tællere - stabile callbacks med useMemo
  const [counts, setCounts] = useState({
    filer: 0, vagter: 0, funktioner: 0, frivillige: 0, teams: 0, adgang: 0, hurtigIndtjekning: 0
  });

  const countCallbacks = useMemo(() => ({
    filer: (count) => setCounts(prev => ({ ...prev, filer: count })),
    vagter: (count) => setCounts(prev => ({ ...prev, vagter: count })),
    funktioner: (count) => setCounts(prev => ({ ...prev, funktioner: count })),
    frivillige: (count) => setCounts(prev => ({ ...prev, frivillige: count })),
    teams: (count) => setCounts(prev => ({ ...prev, teams: count })),
    adgang: (count) => setCounts(prev => ({ ...prev, adgang: count })),
    hurtigIndtjekning: (count) => setCounts(prev => ({ ...prev, hurtigIndtjekning: count })),
  }), []);

  // Preload alle tællere ved mount, så badges (n) viser korrekt uanset hvilken tab brugeren har åbnet sidst.
  // Faner opdaterer dem stadig live via countCallbacks når de monteres/refresher.
  useEffect(() => {
    if (!eventId || !token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const len = (res) => Array.isArray(res?.data) ? res.data.length : 0;
    const safe = (p) => p.then(len).catch(() => 0);

    Promise.all([
      safe(axios.get(`${API}/events/${eventId}/uploaded-files`, auth)),
      safe(axios.get(`${API}/events/${eventId}/shifts`, auth)),
      safe(axios.get(`${API}/events/${eventId}/main-roles`, auth)),
      safe(axios.get(`${API}/events/${eventId}/volunteers`, auth)),
      safe(axios.get(`${API}/events/${eventId}/roles`, auth)),
      // Adgangsstyring tæller alle brugere med adgang (admin-assistenter + arrangører) — kun relevant for admins
      isAdmin
        ? Promise.all([
            safe(axios.get(`${API}/users/admin-assistants`, auth)),
            safe(axios.get(`${API}/users/arrangører`, auth)),
          ]).then(([a, b]) => a + b)
        : Promise.resolve(0),
    ]).then(([filer, vagter, funktioner, frivillige, teams, adgang]) => {
      setCounts({ filer, vagter, funktioner, frivillige, teams, adgang });
    });
  }, [eventId, token, isAdmin]);

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Slet', confirmVariant: 'destructive' });
  const showConfirm = (title, message, onConfirm, opts = {}) => setConfirmDialog({ open: true, title, message, onConfirm, confirmText: opts.confirmText || 'Slet', confirmVariant: opts.confirmVariant || 'destructive' });
  const closeConfirm = () => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Slet', confirmVariant: 'destructive' });

  // Excel upload fra header
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/events/${eventId}/upload-excel`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Excel-fil uploadet");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved upload af Excel");
    }
    e.target.value = '';
  };

  // CSV eksport
  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/volunteers/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Eksporteret ${res.data.count} frivillige`);
    } catch (error) {
      toast.error("Fejl ved eksport");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
        confirmText={confirmDialog.confirmText}
        confirmVariant={confirmDialog.confirmVariant}
      />
      
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">Stamdata</h1>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">{eventName} • Administration</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-excel-btn">
                  <Upload className="h-4 w-4 mr-2" />Upload Excel
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="h-4 w-4 mr-2" />Eksporter CSV
            </Button>
            <div className="text-right">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground font-mono uppercase">{user?.role}</p>
            </div>
            <Button onClick={onLogout} variant="ghost" size="sm">Log ud</Button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="container mx-auto px-4 pb-4">
          <div className="flex gap-2 flex-wrap">
            <Button variant={activeTab === "filer" ? "default" : "outline"} onClick={() => setActiveTab("filer")} data-testid="tab-filer">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Filer ({counts.filer})
            </Button>
            <Button variant={activeTab === "vagter" ? "default" : "outline"} onClick={() => setActiveTab("vagter")} data-testid="tab-vagter">
              <Clock className="h-4 w-4 mr-2" />Vagter ({counts.vagter})
            </Button>
            <Button variant={activeTab === "funktioner" ? "default" : "outline"} onClick={() => setActiveTab("funktioner")} data-testid="tab-funktioner">
              <Briefcase className="h-4 w-4 mr-2" />Funktioner ({counts.funktioner})
            </Button>
            <Button variant={activeTab === "teams" ? "default" : "outline"} onClick={() => setActiveTab("teams")} data-testid="tab-teams">
              <Shield className="h-4 w-4 mr-2" />Teams ({counts.teams})
            </Button>
            {!isArrangør && (
              <Button variant={activeTab === "frivillige" ? "default" : "outline"} onClick={() => setActiveTab("frivillige")} data-testid="tab-frivillige">
                <Users className="h-4 w-4 mr-2" />Frivillige ({counts.frivillige})
              </Button>
            )}
            {!isArrangør && (
              <Button variant={activeTab === "adgang" ? "default" : "outline"} onClick={() => setActiveTab("adgang")} data-testid="tab-adgang">
                <Shield className="h-4 w-4 mr-2" />Adgangsstyring ({counts.adgang})
              </Button>
            )}
            {isAdmin && (
              <Button variant={activeTab === "hurtig-indtjekning" ? "default" : "outline"} onClick={() => setActiveTab("hurtig-indtjekning")} data-testid="tab-hurtig-indtjekning">
                <QrCode className="h-4 w-4 mr-2" />Hurtig indtjekning ({counts.hurtigIndtjekning || 0})
              </Button>
            )}
            <Button variant={activeTab === "materiel" ? "default" : "outline"} onClick={() => setActiveTab("materiel")} data-testid="tab-materiel">
              <Package className="h-4 w-4 mr-2" />Materiel
            </Button>
            <Button variant={activeTab === "ressourcer" ? "default" : "outline"} onClick={() => setActiveTab("ressourcer")} data-testid="tab-ressourcer">
              <Package className="h-4 w-4 mr-2" />Fraktion og containertyper
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8" data-testid="stamdata-content">
        {activeTab === "filer" && (
          <FilerTab token={token} eventId={eventId} eventName={eventName} onCountChange={countCallbacks.filer} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}

        {activeTab === "vagter" && (
          <VagterTab token={token} eventId={eventId} eventName={eventName} onCountChange={countCallbacks.vagter} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}

        {activeTab === "funktioner" && (
          <FunktionerTab token={token} eventId={eventId} onCountChange={countCallbacks.funktioner} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}

        {activeTab === "frivillige" && !isArrangør && (
          <FrivilligeTab token={token} eventId={eventId} eventName={eventName} onCountChange={countCallbacks.frivillige} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}

        {activeTab === "teams" && (
          <TeamsTab token={token} eventId={eventId} eventName={eventName} isAdmin={isAdmin} onCountChange={countCallbacks.teams} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}

        {activeTab === "adgang" && !isArrangør && (
          <AdgangTab token={token} eventId={eventId} onCountChange={countCallbacks.adgang} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}

        {activeTab === "hurtig-indtjekning" && isAdmin && (
          <HurtigIndtjekningTab token={token} eventId={eventId} isAdmin={isAdmin} onCountChange={countCallbacks.hurtigIndtjekning} />
        )}

        {activeTab === "materiel" && (
          <div className="text-center py-16 bg-card border border-border rounded-lg">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Materiel kommer snart</h3>
            <p className="text-muted-foreground">Her vil du kunne administrere materiellister, udstyr og inventar</p>
          </div>
        )}

        {activeTab === "ressourcer" && (
          <RessourcerTab token={token} eventId={eventId} showConfirm={showConfirm} closeConfirm={closeConfirm} />
        )}
      </main>

      <PoweredByFooter />
    </div>
  );
}
